import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import type { DietKind } from '../../../redux/mealPlanUISlice';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { useTranslation } from 'react-i18next';

interface Props {
  label: string;
  selected: boolean;
  portions: number;
  kind: DietKind;
  onPress: () => void;
}

const KIND_LABEL: Record<DietKind, string> = {
  auto: 'mealConfig.auto',
  fish: 'mealConfig.fish',
  veg: 'mealConfig.veggie',
  meat: 'mealConfig.meat',
};

function kindToDietaryRole(kind: DietKind): DietaryRole | undefined {
  if (kind === 'fish') return 'fish';
  if (kind === 'veg') return 'vegetarian';
  return undefined;
}

export function ConfigRow({ label, selected, portions, kind, onPress }: Props) {
  const { t } = useTranslation();
  return (
    <Pressable
      style={[styles.row, selected ? styles.selectedRow : styles.unselectedRow]}
      onPress={onPress}
    >
      {selected ? (
        <>
          <View style={styles.checkCircle}>
            <Text style={styles.checkMark}>✓</Text>
          </View>
          <Text style={styles.mealLabel}>{label}</Text>
          <View style={styles.selectedMeta}>
            <Text style={styles.portionsText}>{portions} pers.</Text>
            {kind !== 'auto' && (
              <View style={styles.kindRow}>
                <DietDot role={kindToDietaryRole(kind)} size={6} />
                <Text style={styles.kindText}>{t(KIND_LABEL[kind])}</Text>
              </View>
            )}
          </View>
        </>
      ) : (
        <>
          <Text style={styles.plusIcon}>+</Text>
          <Text style={styles.unselectedLabel}>{label.toUpperCase()}</Text>
          <Text style={styles.suggestHint}>{t('thisWeek.suggestAction')}</Text>
        </>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    borderWidth: 1.5,
    paddingHorizontal: 14,
    paddingVertical: 13,
    gap: 10,
    marginBottom: 8,
  },
  unselectedRow: {
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    backgroundColor: Paprika.bgSunk,
  },
  selectedRow: {
    borderStyle: 'solid',
    borderColor: Paprika.brandSoft,
    backgroundColor: Paprika.brandTint,
  },
  checkCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkMark: {
    color: '#fff',
    fontSize: 12,
    fontFamily: 'Inter-SemiBold',
  },
  mealLabel: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
  selectedMeta: {
    alignItems: 'flex-end',
    gap: 2,
  },
  portionsText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  kindRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kindText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  plusIcon: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textMuted,
    lineHeight: 20,
  },
  unselectedLabel: {
    flex: 1,
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.5,
    color: Paprika.textMuted,
  },
  suggestHint: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
});
